import { DOCUMENT } from '@angular/common';
import { Component, Input, inject } from '@angular/core';

import { EdpService } from '@bytel/checkout';

import { ModalService } from '@base/common/services/modal.service';

import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { PaymentService } from '@services/payment.service';
import { StepperService } from '@services/stepper.service';

import { ScoringActions } from '@interfaces/payment.interface';
import { ScoringAction, ScoringResult, ScoringScope } from '@interfaces/scoring.interface';

import { ByouErrorModalComponent } from '@shared/byou-error-modal/byou-error-modal.component';
import { ErrorModalComponent } from '@shared/error-modal/error-modal.component';
import { ModalComponent } from '@shared/modal/modal.component';
import { ACTION_CLICK } from '@shared/tag/tag.interface';

import { Subject, Subscription, takeUntil } from 'rxjs';

import { TicketComponent } from '../../../../shared/ticket/ticket.component';

@Component({
    selector: 'checkout-scoring-modal',
    templateUrl: './scoring-modal.component.html',
    styleUrl: 'scoring-modal.component.scss',
    standalone: true,
    imports: [TicketComponent, ModalComponent],
})
export class ScoringModalComponent extends ModalComponent {
    public static readonly cannotBeOverriden: boolean = true;

    @Input() public scoringResults: ScoringResult[];
    public modalRef: ModalService;
    public scoringActions = ScoringActions;
    public afterClosedSubscription: Subscription;
    public choice: boolean = false;
    public messages: string[];
    public showPayment: boolean = false;
    public showUnpaid: boolean = false;
    public title = 'Information';
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    private cartService: MainCartService = inject(MainCartService);
    private edpService: EdpService = inject(EdpService);
    private stepperService: StepperService = inject(StepperService);
    private messageService: MessageService = inject(MessageService);
    private paymentService: PaymentService = inject(PaymentService);
    private document: Document = inject(DOCUMENT);
    private configService: ConfigurationService = inject(ConfigurationService);

    public ngOnInit(): void {
        this.updatePayment();
        this.messages = this.scoringResults
            ?.filter((result) => result.applicationScope.type === ScoringScope.MARKET_LINE)
            .map((result) => result.message);
        this.modalRef.afterClosed.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            if (!this.choice) {
                this.errorRedirect();
            }
        });

        try {
            this.tagService.sendEvent('Scoring_KO', 'Paiement_retour_scoring', ACTION_CLICK.PRINTING);
        } catch (error) {
            console.log(error);
        }
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public close(): void {
        this.modalRef.close();
    }

    public errorRedirect(): void {
        this.close();
        this.resetCart();
        this.document.defaultView.location.href = this.configService.getData().global.origin;
    }

    /**
     * Remove EDP or Funding and pay with cash
     */
    public payWithCash(): void {
        this.choice = true;

        this.cartService.setFundingMethod(this.paymentService.fundingCashMethod).subscribe({
            next: () => {
                this.stepperService.validateSubject.next(true);
                this.close();
            },
            error: () => {
                this.close();
                this._showErrorModal();
            },
        });
    }

    public redirectToStore(): void {
        // eslint-disable-next-line dot-notation
        this.document.defaultView.location.href = this.configService.getData().global.wall['stores'];
    }

    public redirectToAccount(): void {
        // eslint-disable-next-line dot-notation
        this.document.defaultView.location.href = this.configService.getData().global.wall['account'];
    }

    private updatePayment(): void {
        const actionResult = this.scoringResults?.find(
            (sr) => sr.applicationScope.type === ScoringScope.REQUESTED_ACT,
        )?.actions;

        if (actionResult?.length > 0) {
            this.showUnpaid = actionResult[0].type === ScoringAction.REGULARISATION_IMPAYE;
            if (actionResult[0].type === ScoringAction.RETRAIT_EDP) {
                this.showPayment = true;
                this.edpService.removeEdpProduct(this.cartService.cartModel).subscribe();
            }

            if (actionResult[0].type === ScoringAction.RETRAIT_CREDIT) {
                this.showPayment = true;
                this.cartService.setFundingMethod(this.paymentService.fundingCashMethod).subscribe();
            }
        }
    }

    private resetCart(): void {
        this.cartService.reset();
        this.cartService.addQuote();
        this.cartService.refresh();
    }

    private _showErrorModal(): void {
        if (this.cartService.isSowo()) {
            this.messageService.openModal(ByouErrorModalComponent);
        } else {
            this.messageService.openModal(ErrorModalComponent);
        }
    }
}
